import AppContext from 'App/AppContext';
import { AIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { AIActionKind } from 'models/copilot/actions/aiActionTemplate';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getCopilotPanelContextDefinitionMap } from './Panel/Runtime/copilotPanelContextDefinitions';
import { isCopilotChatAvailable, isCopilotPanelAvailable } from './copilotPanelAvailability';

/**
 * The assistant bindings the current scene supports, for a scene's AI actions menu. They are the very
 * bindings the panel lists for the active usage, so labels and gating cannot drift apart: the
 * definition decides what it offers, this hook filters it to what belongs on this page.
 *
 * A prompt whose template takes a selection is worded by the usage on the ids the scene passes in and
 * carries them to the panel; without a selection it keeps its own label and asks about the whole set.
 */
export const useCopilotAssistantBindings = (selectedEntityIds?: number[]): AIActionBinding[] => {
  const { t } = useTranslation(['copilot']);
  const appContext = useContext(AppContext);
  const { pathname } = useLocation();
  // the per-context getter builds every definition on each call
  const definitions = useMemo(() => getCopilotPanelContextDefinitionMap(), []);
  // a new array of the same ids is the same selection
  const selectionKey: string = (selectedEntityIds ?? []).join(',');
  const passesSelection = selectedEntityIds !== undefined;

  // The panel's own buttons run with the selection of this moment too: a list scene's selection is kept on the
  // bridge for the click. Only a caller that passes a selection sets it, and its unmount clears it.
  const selectionSetterRef = useRef({});
  const copilotBridge = appContext.copilotBridge;
  useEffect(() => {
    if (!passesSelection) return;

    copilotBridge.setSceneSelection(
      selectionSetterRef.current,
      selectionKey ? selectionKey.split(',').map(Number) : [],
    );
  }, [copilotBridge, passesSelection, selectionKey]);
  useEffect(() => {
    const setter = selectionSetterRef.current;

    return () => copilotBridge.clearSceneSelection(setter);
  }, [copilotBridge]);

  return useMemo(() => {
    if (!isCopilotPanelAvailable(appContext)) return [];

    const copilotBridge = appContext.copilotBridge;
    const activeContent = copilotBridge.activeContent;
    const definition = definitions.get(copilotBridge.activeContext);
    if (!definition?.isAvailableForContent(activeContent)) return [];

    // A prompt is chat: without the general knowledge chat the panel would refuse the question.
    const chatEnabled = isCopilotChatAvailable(appContext);
    const selection: number[] = selectedEntityIds ?? [];

    return (
      definition
        .getBindings(activeContent)
        .filter((binding) => binding.disabled !== true)
        .filter(
          (binding) =>
            binding.template.kind === AIActionKind.Process ||
            (binding.template.kind === AIActionKind.Prompt && chatEnabled),
        )
        // A scene menu offers what belongs to the page it sits on. A binding without a route belongs
        // wherever its usage is active; one with a route belongs where its result shows up.
        .filter((binding) => binding.route === undefined || binding.route.split('#')[0] === pathname)
        // a prompt about the selected rows is offered while rows are selected
        .filter(
          (binding) =>
            binding.template.kind !== AIActionKind.Prompt ||
            !binding.template.requiresSelection ||
            selection.length > 0,
        )
        .map((binding) => {
          // a process start that takes the selection keeps its label: the rows only travel
          if (binding.template.kind === AIActionKind.Process && binding.template.acceptsSelection === true) {
            return { ...binding, selectedEntityIds: selection };
          }

          const takesSelection: boolean =
            binding.template.kind === AIActionKind.Prompt && binding.template.acceptsSelection === true;
          if (!takesSelection) return binding;

          return {
            ...binding,
            label:
              selection.length > 0 ? definition.getEntitySelectionQuestion(selection, t, activeContent) : undefined,
            selectedEntityIds: selection,
          };
        })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext, definitions, pathname, selectionKey, t]);
};
